import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  AlertCircleIcon, OctagonX, PauseCircle,
  Globe, Mail, Phone, Building2, X,
} from 'lucide-react'
import { ApiError, apiRequest } from '@/lib/api'
import { PublicPageControls } from '@/components/app/PublicPageControls'
import { PublicFooter } from '@/components/app/PublicFooter'
import { AuthAlert } from '@/features/auth/components/AuthAlert'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'

type ProgramStatus = 'active' | 'paused' | 'suspended'

interface PortalInfo {
  business_name: string
  business_logo_url: string | null
  business_website_url: string | null
  business_contact_email: string | null
  business_contact_phone: string | null
  program_name: string
  program_description: string | null
  program_status: ProgramStatus
}

interface PortalInfoEnvelope { data: PortalInfo }

interface PublicProspectPayload {
  agent_code: string
  program_id: string
  contact_name: string
  contact_email: string | null
  contact_phone_raw: string | null
  company_name: string | null
}

type FieldErrors = Record<string, string>

async function fetchPortalInfo(programId: string, agentCode: string): Promise<PortalInfo> {
  const res = await apiRequest<PortalInfoEnvelope>(
    `/public/programs/${encodeURIComponent(programId)}/portal-info?agent_code=${encodeURIComponent(agentCode)}`,
  )
  return res.data
}

async function submitPublicProspect(payload: PublicProspectPayload) {
  return apiRequest('/public/prospects', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
}

/* ── Top navbar ── */
function PortalNav() {
  const { t } = useTranslation()
  return (
    <header className="fixed inset-x-0 top-0 z-30 flex h-12 items-center justify-between px-4 sm:px-6">
      <img src="/Uploads/logo-mark-light.svg" alt={t('publicPortal.logoAlt')} className="h-7 w-auto dark:hidden" />
      <img src="/Uploads/logo-mark-dark.svg" alt={t('publicPortal.logoAlt')} className="hidden h-7 w-auto dark:block" />
      <PublicPageControls />
    </header>
  )
}

/* ── Skeleton loading state (mirrors portal layout) ── */
function PortalSkeleton() {
  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* nav skeleton */}
      <div className="flex h-12 shrink-0 items-center justify-between px-4 sm:px-6">
        <Skeleton className="h-7 w-7 rounded-md" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-7 w-8 rounded-md" />
          <Skeleton className="h-7 w-8 rounded-md" />
        </div>
      </div>

      {/* main content */}
      <div className="flex flex-1 flex-col items-center justify-center px-4 pb-10">
        <div className="w-full max-w-sm space-y-6">
          {/* business identity */}
          <div className="flex flex-col items-center gap-3 pt-4">
            <Skeleton className="size-16 rounded-[0.625rem]" />
            <div className="flex flex-col items-center gap-2">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-5 w-48" />
              <Skeleton className="h-3 w-36" />
            </div>
          </div>

          {/* form card */}
          <div className="rounded-[0.625rem] bg-card p-5 space-y-4">
            <Skeleton className="h-4 w-36" />
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-9 w-full rounded-md" />
              </div>
            ))}
            <Skeleton className="h-9 w-full rounded-md mt-2" />
          </div>

          {/* contact CTA */}
          <Skeleton className="h-9 w-full rounded-[0.625rem]" />
        </div>

        {/* footer skeleton */}
        <div className="mt-8 flex flex-col items-center gap-2">
          <div className="flex items-center gap-3">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-12" />
          </div>
          <Skeleton className="h-3 w-48" />
        </div>
      </div>
    </div>
  )
}

/* ── Business identity dialog ── */
function BusinessDialog({
  info,
  open,
  onClose,
}: {
  info: PortalInfo
  open: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  if (!open) return null
  const hasContact = info.business_website_url || info.business_contact_email || info.business_contact_phone

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative z-10 w-full max-w-sm rounded-t-[0.625rem] sm:rounded-[0.625rem] border border-border bg-background p-6 mx-0 sm:mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            {info.business_logo_url ? (
              <img
                src={info.business_logo_url}
                alt={info.business_name}
                className="size-10 rounded-[0.625rem] border border-border object-contain bg-background"
              />
            ) : (
              <div className="flex size-10 items-center justify-center rounded-[0.625rem] border border-border bg-muted">
                <Building2 className="size-5 text-muted-foreground" />
              </div>
            )}
            <div>
              <p className="text-sm font-semibold text-foreground">{info.business_name}</p>
              <p className="text-xs text-muted-foreground">{info.program_name}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex size-7 items-center justify-center rounded-[0.625rem] hover:bg-muted text-muted-foreground transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        {hasContact ? (
          <div className="space-y-2.5">
            <p className="text-xs text-muted-foreground mb-3">{t('publicPortal.contactInfo')}</p>
            {info.business_website_url && (
              <a
                href={info.business_website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 rounded-[0.625rem] border border-border px-3 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <Globe className="size-4 text-muted-foreground shrink-0" />
                <span className="truncate">{info.business_website_url.replace(/^https?:\/\//, '')}</span>
              </a>
            )}
            {info.business_contact_email && (
              <a
                href={`mailto:${info.business_contact_email}`}
                className="flex items-center gap-3 rounded-[0.625rem] border border-border px-3 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <Mail className="size-4 text-muted-foreground shrink-0" />
                <span className="truncate">{info.business_contact_email}</span>
              </a>
            )}
            {info.business_contact_phone && (
              <a
                href={`tel:${info.business_contact_phone}`}
                className="flex items-center gap-3 rounded-[0.625rem] border border-border px-3 py-2.5 text-sm text-foreground hover:bg-muted transition-colors"
              >
                <Phone className="size-4 text-muted-foreground shrink-0" />
                <span className="truncate">{info.business_contact_phone}</span>
              </a>
            )}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-2">{t('publicPortal.noContactInfo')}</p>
        )}
      </div>
    </div>
  )
}

/* ── Status screens (paused / suspended / error) ── */
function StatusScreen({
  info,
  icon,
  title,
  description,
  tone,
}: {
  info?: PortalInfo
  icon: React.ReactNode
  title: string
  description: string
  tone: 'amber' | 'orange' | 'red'
}) {
  const toneClasses = {
    amber: 'border-amber-200 bg-amber-50 dark:border-amber-800/40 dark:bg-amber-950/20',
    orange: 'border-orange-200 bg-orange-50 dark:border-orange-800/40 dark:bg-orange-950/20',
    red: 'border-destructive/30 bg-destructive/5',
  }

  return (
    <>
      <PortalNav />
      <main className="flex min-h-screen flex-col items-center justify-center bg-muted dark:bg-background px-4 pt-12">
        <div className="w-full max-w-sm py-10 space-y-6 text-center">
          {info && (
            <div className="space-y-2">
              {info.business_logo_url && (
                <img
                  src={info.business_logo_url}
                  alt={info.business_name}
                  className="mx-auto size-14 rounded-[0.625rem] border border-border object-contain"
                />
              )}
              <p className="text-xs text-muted-foreground">{info.business_name}</p>
              <p className="text-base font-semibold text-foreground">{info.program_name}</p>
            </div>
          )}
          <div className={`rounded-[0.625rem] border p-5 space-y-2 ${toneClasses[tone]}`}>
            {icon}
            <p className="text-sm font-medium text-foreground">{title}</p>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>
        <PublicFooter />
      </main>
    </>
  )
}

/* ── Main page ── */
export function PublicProspectPortalPage() {
  const { t } = useTranslation()
  const { agentCode = '', programId = '' } = useParams<{ agentCode: string; programId: string }>()

  const [portalInfo, setPortalInfo] = useState<PortalInfo | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const [contactName, setContactName] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [clientError, setClientError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [bizDialogOpen, setBizDialogOpen] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    fetchPortalInfo(programId, agentCode)
      .then((info) => { if (!cancelled) { setPortalInfo(info); setLoading(false) } })
      .catch((err: unknown) => {
        if (!cancelled) {
          setLoadError(err instanceof ApiError ? err.message : t('publicPortal.invalidDescription'))
          setLoading(false)
        }
      })
    return () => { cancelled = true }
  }, [agentCode, programId, t])

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!contactEmail.trim() && !contactPhone.trim()) {
      setClientError(t('publicPortal.contactRequiredDescription'))
      return
    }
    setClientError(null)
    setFieldErrors({})
    setSubmitError(null)
    setSubmitting(true)
    try {
      await submitPublicProspect({
        agent_code: agentCode,
        program_id: programId,
        contact_name: contactName.trim(),
        contact_email: contactEmail.trim() || null,
        contact_phone_raw: contactPhone.trim() || null,
        company_name: companyName.trim() || null,
      })
      setSubmitted(true)
    } catch (err: unknown) {
      const errors = err instanceof ApiError ? (err.errors ?? {}) : {}
      const flat: FieldErrors = {}
      for (const [key, messages] of Object.entries(errors)) {
        flat[key] = messages[0] ?? ''
      }
      setFieldErrors(flat)
      if (Object.keys(flat).length === 0) {
        setSubmitError(err instanceof ApiError ? err.message : t('publicPortal.submitErrorDescription'))
      }
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return <PortalSkeleton />
  }

  if (loadError || !portalInfo) {
    return (
      <StatusScreen
        icon={<AlertCircleIcon className="mx-auto size-9 text-destructive" />}
        title={t('publicPortal.invalidTitle')}
        description={loadError ?? t('publicPortal.invalidDescription')}
        tone="red"
      />
    )
  }

  if (portalInfo.program_status === 'paused') {
    return (
      <StatusScreen
        info={portalInfo}
        icon={<PauseCircle className="mx-auto size-9 text-amber-500" />}
        title={t('publicPortal.pausedTitle')}
        description={t('publicPortal.pausedDescription')}
        tone="amber"
      />
    )
  }

  if (portalInfo.program_status === 'suspended') {
    return (
      <StatusScreen
        info={portalInfo}
        icon={<OctagonX className="mx-auto size-9 text-orange-500" />}
        title={t('publicPortal.suspendedTitle')}
        description={t('publicPortal.suspendedDescription')}
        tone="orange"
      />
    )
  }

  if (submitted) {
    return (
      <>
        <PortalNav />
        <main className="flex min-h-screen flex-col items-center justify-center bg-muted dark:bg-background px-4 pt-12">
          <div className="w-full max-w-sm py-10">
            <AuthAlert variant="success" title={t('publicPortal.successTitle')}>
              {t('publicPortal.successDescription')}
            </AuthAlert>
          </div>
          <PublicFooter />
        </main>
      </>
    )
  }

  const hasAnyContact = portalInfo.business_website_url
    || portalInfo.business_contact_email
    || portalInfo.business_contact_phone

  return (
    <>
      <PortalNav />

      <main className="flex min-h-screen flex-col items-center justify-center bg-muted dark:bg-background px-4 pt-12 pb-10">
        <div className="w-full max-w-sm space-y-6">

          {/* ── Business identity ── */}
          <div className="flex flex-col items-center gap-3 pt-4 text-center">
            {portalInfo.business_logo_url ? (
              <img
                src={portalInfo.business_logo_url}
                alt={portalInfo.business_name}
                className="size-16 rounded-[0.625rem] border border-border object-contain bg-background"
              />
            ) : (
              <div className="flex size-16 items-center justify-center rounded-[0.625rem] border border-border bg-muted">
                <Building2 className="size-8 text-muted-foreground" />
              </div>
            )}
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">{portalInfo.business_name}</p>
              <h1 className="text-lg font-semibold text-foreground leading-snug">{portalInfo.program_name}</h1>
              {portalInfo.program_description && (
                <p className="text-sm text-muted-foreground leading-relaxed">{portalInfo.program_description}</p>
              )}
            </div>
          </div>

          {/* ── Form ── */}
          <div className="rounded-[0.625rem] bg-card p-5">
            <p className="text-sm font-medium text-foreground mb-4">{t('publicPortal.formTitle')}</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground" htmlFor="pub-contact-name">
                  {t('publicPortal.fields.contactNameLabel')} <span className="text-destructive">*</span>
                </label>
                <Input
                  id="pub-contact-name"
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                  placeholder={t('publicPortal.fields.contactNamePlaceholder')}
                  required
                />
                {fieldErrors.contact_name && <p className="text-xs text-destructive">{fieldErrors.contact_name}</p>}
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground" htmlFor="pub-company">
                  {t('publicPortal.fields.companyLabel')}
                </label>
                <Input
                  id="pub-company"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder={t('publicPortal.fields.companyPlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground" htmlFor="pub-email">
                  {t('publicPortal.fields.emailLabel')}
                </label>
                <Input
                  id="pub-email"
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder={t('publicPortal.fields.emailPlaceholder')}
                />
                {fieldErrors.contact_email && <p className="text-xs text-destructive">{fieldErrors.contact_email}</p>}
              </div>

              <div className="space-y-2">
                <label className="text-sm text-muted-foreground" htmlFor="pub-phone">
                  {t('publicPortal.fields.phoneLabel')}
                </label>
                <Input
                  id="pub-phone"
                  value={contactPhone}
                  onChange={(e) => setContactPhone(e.target.value)}
                  placeholder={t('publicPortal.fields.phonePlaceholder')}
                />
                {fieldErrors.contact_phone_raw && <p className="text-xs text-destructive">{fieldErrors.contact_phone_raw}</p>}
              </div>

              <p className="text-xs text-muted-foreground">{t('publicPortal.contactHint')}</p>

              {clientError && (
                <Alert variant="destructive">
                  <AlertCircleIcon />
                  <AlertTitle>{t('publicPortal.contactRequiredTitle')}</AlertTitle>
                  <AlertDescription>{clientError}</AlertDescription>
                </Alert>
              )}

              {submitError && (
                <Alert variant="destructive">
                  <AlertCircleIcon />
                  <AlertTitle>{t('publicPortal.submitErrorTitle')}</AlertTitle>
                  <AlertDescription>{submitError}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={submitting || !contactName.trim()}>
                {submitting ? t('publicPortal.submitting') : t('publicPortal.submit')}
              </Button>
            </form>
          </div>

          {/* ── Business contact CTA ── */}
          {hasAnyContact && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => setBizDialogOpen(true)}
            >
              <Building2 className="size-4 shrink-0" />
              {t('publicPortal.contactBusiness', { businessName: portalInfo.business_name })}
            </Button>
          )}
        </div>
        <div className="mt-8">
          <PublicFooter />
        </div>
      </main>

      <BusinessDialog
        info={portalInfo}
        open={bizDialogOpen}
        onClose={() => setBizDialogOpen(false)}
      />
    </>
  )
}
